import React, { useEffect, useMemo, useState, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Collapse,
  Menu,
  Modal,
  Button,
  Skeleton,
} from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

import LayerdIcon from "../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../icons/uiLucideIcons";

import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import { generateMenuSx } from "../../../../layouts/_new/Normal";

import ManageDomainModal from "./ManageDomain";
import AddDomainWebsiteInformationModal from "./AddDomainWebsiteInformationModal";
import SitesFilterBar from "./SitesFilterBar";
import { useNotification } from "../../../../contexts/NotificationContext";
import { UserType } from "../../../../utils/constants";
import { getStatusString } from "../../../../utils/functions";
import { CancelButton, SaveButton, containedButtonSx } from "../../../common/Button";
import { buildOnboardedAtById, getSiteRowActionKind, mergeResumeSiteFromList } from "./addDomain2Gates";
import { formatDate, displayCell } from "../../../../utils/format";
import useAuth from "../../../../hooks/useAuth";
import useSite from "../../../../hooks/user/useSite";
import useHome from "../../../../hooks/user/useHome";
import usePermissions from "../../../../hooks/usePermissions";
import { rememberSingleSiteId } from "../../../../utils/globalSite";
import { PERMISSIONS } from "../../../../utils/permissions";
import userAxios from "../../../../utils/axios/v1/userAxios";
import PermissionGate from "../../../guards/PermissionGate";

const NoMaxWidthTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
    fontSize: 12,
    lineHeight: "18px",
    color: colors.neutral[100],
    backgroundColor: colors.neutral[1000],
    py: 0.5,
  },
});

export const getStatusLabel = (isDeleted) => {
  const label = isDeleted ? "Disabled" : "Active";
  let backgroundColor = label === "Active" ? colors.green[10] : colors.red[10];
  let borderColor = label === "Active" ? colors.green[100] : colors.red[100];
  let fontColor = label === "Active" ? colors.green[300] : colors.red[100];
  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      sx={{ minWidth: 80, width: 80, height: 25, bgcolor: backgroundColor, borderRadius: "20px", border: `1px solid ${borderColor}` }}
    >
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: fontColor }}>{label}</Typography>
    </Stack>
  );
};

export const getEnforcementLabel = (status) => {
  if (status === null || status === undefined) return null;
  const label = getStatusString(status);
  let backgroundColor = label === "Enforced" ? colors.green[10] : label === "Unhealthy" ? "#FFF6D0" : colors.neutral[10];
  let borderColor = label === "Enforced" ? colors.green[100] : label === "Unhealthy" ? colors.yellow[200] : colors.neutral[400];
  let fontColor = label === "Enforced" ? colors.green[300] : label === "Unhealthy" ? "#A68705" : colors.neutral[700];
  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      sx={{ minWidth: 80, width: 80, height: 25, bgcolor: backgroundColor, borderRadius: "20px", border: `1px solid ${borderColor}` }}
    >
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: fontColor }}>{label}</Typography>
    </Stack>
  );
};

const cellTypographyStyle =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] };
const headTypographyStyle = {
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: "medium",
  color: colors.neutral[1000],
};

const SITES_PAGE_SIZE_OPTIONS = [10, 25, 50];
const SITES_DEFAULT_PAGE_SIZE = 10;
const sitesTablePaginationSx = {
  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
    fontSize: 14, color: colors.neutral[700],
  },
};

const NewSitesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
    setWebsiteController,
  } = useAuth();
  const { sites, sites_total, getSites, getSitesForList, errMsg, setErr, site_List, deleteSite, updateSite, viewSite, selectSite } = useSite();
  const { showError, showSuccess, showInfo } = useNotification();
  // The Enforcement column's value (`site.status`) is only populated by
  // the rich `GET /site` fetch, not the fast `/site/basis_all` fetch.
  // Track whether the rich fetch has landed at least once so the cell
  // can distinguish "still loading" (skeleton) from "loaded, no value"
  // (dash) — previously both rendered as an indefinite skeleton, which
  // meant a genuinely-null status was indistinguishable from a slow
  // network call.
  const enforcementLoading = sites === null || sites === undefined;
  const [unDeletedSites, setUnDeletedSites] = useState([]);
  const { curProjectId, applySiteSelectorUi, getAllProjectBaseList } = useHome();
  const { hasPermission, hasRolePermission, getPermissionsForSite, PERMISSIONS: PERMS, user } = usePermissions();

  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("q") || "";
    } catch (_) {
      return "";
    }
  });
  const [statusFilter, setStatusFilter] = useState("active");
  const [enforcementFilter, setEnforcementFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(SITES_DEFAULT_PAGE_SIZE);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => { setPage(0); }, [debouncedSearch, statusFilter, enforcementFilter, curProjectId]);

  const listQueryOpts = useMemo(() => {
    const opts = {
      page,
      limit: pageSize,
      status: statusFilter || "active",
      enforcement: enforcementFilter || "all",
    };
    if (curProjectId && curProjectId !== "all") {
      opts.project_id = curProjectId === "default" ? "default" : curProjectId;
    }
    if (debouncedSearch.trim()) opts.q = debouncedSearch.trim();
    return opts;
  }, [page, pageSize, statusFilter, enforcementFilter, curProjectId, debouncedSearch]);

  const onboardedAtById = useMemo(() => buildOnboardedAtById(site_List), [site_List]);

  // Keep deleted sites in the list so Status Active/Disabled can filter client-side.
  // Default statusFilter is "active" (see below) to preserve the previous default view.
  useEffect(() => {
    if (sites === null || sites === undefined) {
      if (site_List === null || site_List === undefined) {
        setUnDeletedSites(null);
      } else {
        setUnDeletedSites(site_List);
      }
    } else {
      setUnDeletedSites(sites);
    }
  }, [sites, site_List]);

  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  const isInitialLoad = sites === null && site_List === null;
  const [isListRefreshing, setIsListRefreshing] = useState(false);
  const showTableLoadingSkeleton = isInitialLoad || isListRefreshing;

  const refreshSitesPage = useCallback(
    async (opts = listQueryOpts, { withBasis = false, refreshing = true } = {}) => {
      if (refreshing) setIsListRefreshing(true);
      try {
        const jobs = [getSites(setWebsiteController, opts)];
        if (withBasis) jobs.push(getSitesForList());
        await Promise.all(jobs);
      } finally {
        if (refreshing) setIsListRefreshing(false);
      }
    },
    [getSites, getSitesForList, listQueryOpts, setWebsiteController]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const searchParams = new URLSearchParams(location.search);
    const forceRefresh = searchParams.get("refresh");

    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (websiteController) websiteController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();

      if (forceRefresh) {
        refreshSitesPage(listQueryOpts, { withBasis: true, refreshing: true }).finally(() => {
          navigate("/v2/sites", { replace: true });
        });
      }
    }
    return () => setErr(null);
  }, [isAuthenticated, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) return;
    const withBasis = site_List === null;
    refreshSitesPage(listQueryOpts, { withBasis, refreshing: true });
  }, [listQueryOpts, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const [openAddDomain2Modal, setOpenAddDomain2Modal] = useState(false);
  const [resumeSite, setResumeSite] = useState(null);
  const handleAddDomain2Open = () => {
    setResumeSite(null);
    setOpenAddDomain2Modal(true);
  };
  const handleAddDomain2Close = () => {
    setOpenAddDomain2Modal(false);
    setResumeSite(null);
  };
  const handleResumeSetup = (event, site) => {
    event.stopPropagation();
    setResumeSite(mergeResumeSiteFromList(site, site_List));
    setOpenAddDomain2Modal(true);
  };
  const handleAddDomain2Created = async (createdSite, { selectAsCurrent = false } = {}) => {
    try {
      // Refresh list APIs and project→site map so the new site appears without a full reload.
      await refresh({ refreshProjectMap: true });
    } catch (e) {
      showError(e?.message || "Failed to refresh sites list");
    }
    // After Finish, pin the new site so WAF / services pages don't auto-pick the first site.
    if (!selectAsCurrent) return;
    const siteId = createdSite?.site_id;
    if (!siteId || siteId === "all") return;
    rememberSingleSiteId(siteId);
    try {
      localStorage.setItem("curSiteId", siteId);
    } catch {
      /* ignore */
    }
    applySiteSelectorUi({
      enabled: true,
      showAllOption: false,
      curSiteId: siteId,
    });
    try {
      await selectSite(siteId);
    } catch {
      /* selectSite already surfaces failures via context */
    }
  };

  // const downloadWebsites = () => {
  //   var curDate = new Date();
  //   const input = document.getElementsByClassName("MuiTableContainer-root")[0];
  //   html2canvas(input).then((canvas) => {
  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF({
  //       orientation: input.offsetWidth > input.offsetHeight ? "l" : "p",
  //       unit: "px",
  //       format: [input.offsetWidth + 240, input.offsetHeight + 180],
  //     });
  //     pdf.addImage(imgData, "JPEG", 120, 90, input.offsetWidth, input.offsetHeight);
  //     pdf.save(`LD_Websites (` + formatDate(curDate) + `).pdf`);
  //   });
  // };

  const refresh = async ({ refreshProjectMap = false } = {}) => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (websiteController) websiteController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      setIsListRefreshing(true);
      try {
        const jobs = [getSitesForList(), getSites(setWebsiteController, listQueryOpts)];
        if (refreshProjectMap && typeof getAllProjectBaseList === "function") {
          jobs.push(getAllProjectBaseList(true));
        }
        await Promise.all(jobs);
      } finally {
        setIsListRefreshing(false);
      }
    }
  };
  const handleClick = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const [siteListToShow, setSiteListToShow] = useState(null);

  useEffect(() => {
    if (!unDeletedSites) {
      setSiteListToShow([]);
      return;
    }
    const filteredSites = unDeletedSites
      .filter((site) => site.created_date)
      .map((site) => ({
        ...site,
        subdomains: (site.subdomains || []).filter((sub) => sub.updated_date),
      }));
    setSiteListToShow(filteredSites);
  }, [unDeletedSites]);

  // Server already applied project/status/enforcement/q + page; these are the current page rows.
  const pagedVisibleSites = siteListToShow || [];
  const filteredVisibleSites = pagedVisibleSites;
  const sitesTotalCount = typeof sites_total === "number" ? sites_total : pagedVisibleSites.length;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil((sitesTotalCount || 0) / pageSize) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [sitesTotalCount, page, pageSize]);

  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
    statusFilter !== "active" ||
    enforcementFilter !== "all"
  );

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("active");
    setEnforcementFilter("all");
  };

  const [actionMainDomainMenu, setActionMainDomainMenu] = useState({
    anchorEl: null,
    siteId: null,
  });
  const handleActionMainDomainOpen = (event, siteId) => {
    event.stopPropagation();
    setActionMainDomainMenu({ anchorEl: event.currentTarget, siteId });
  };
  const handleActionMainDomainClose = () => {
    setActionMainDomainMenu({ anchorEl: null, siteId: null });
  };
  const isActionMainDomainMenuOpen = Boolean(actionMainDomainMenu.anchorEl);

  const [actionSubdomainMenu, setActionSubdomainMenu] = useState({
    anchorEl: null,
    siteId: null,
    subdomainId: null,
  });

  const handleActionSubdomainOpen = (event, siteId, subdomainId = null) => {
    event.stopPropagation();
    setActionSubdomainMenu({ anchorEl: event.currentTarget, siteId, subdomainId });
  };

  // close handler
  const handleActionSubdomainClose = () => {
    setActionSubdomainMenu({ anchorEl: null, siteId: null, subdomainId: null });
  };

  const isActionSubdomainMenuOpen = Boolean(actionSubdomainMenu.anchorEl);

  const [selectedSite, setSelectedSite] = useState(null);

  const [openManageDomainModal, setOpenManageDomainModal] = useState(false);
  const handleManageDomainOpen = () => setOpenManageDomainModal(true);
  const handleManageDomainClose = () => setOpenManageDomainModal(false);

  const [siteToDelete, setSiteToDelete] = useState(null);
  const [openSiteToDelete, setOpenSiteToDelete] = useState(false);
  const handleSiteToDeleteOpen = () => setOpenSiteToDelete(true);
  const handleSiteToDeleteClose = () => setOpenSiteToDelete(false);

  const [subdomainToDelete, setSubdomainToDelete] = useState(null);
  const [openSubdomainToDelete, setOpenSubdomainToDelete] = useState(false);
  const handleSubdomainToDeleteOpen = () => setOpenSubdomainToDelete(true);
  const handleSubdomainToDeleteClose = () => setOpenSubdomainToDelete(false);

  const [loading, setLoading] = useState(false);
  const [disablingSiteId, setDisablingSiteId] = useState(null);

  const handleDisableDomain = async (site) => {
    if (!site?.id || disablingSiteId) return;
    handleActionMainDomainClose();

    const disabled = [];
    setDisablingSiteId(site.id);

    try {
      let apiSecEnabled = false;
      try {
        const r = await userAxios.get(`/asec/${site.id}/master`);
        apiSecEnabled = !!r.data?.enabled;
      } catch {
        apiSecEnabled = false;
      }

      if (apiSecEnabled) {
        await userAxios.put(`/asec/${site.id}/master`, { enabled: false });
        disabled.push("API Security");
      }

      let domainProtectionActive = site.enabled;
      if (domainProtectionActive === undefined) {
        try {
          const detail = await viewSite(site.id);
          domainProtectionActive = detail?.enabled;
        } catch {
          domainProtectionActive = true;
        }
      }

      const wwwSub = site.subdomains?.find((sub) => sub.name === "www");
      const wwwActive = !!wwwSub?.enabled;

      const sitePayload = {};
      if (domainProtectionActive !== false) {
        sitePayload.enable = false;
        sitePayload.site_addr = site.addr;
        disabled.push("Domain Protection");
      }

      let subdomainsPayload = null;
      if (wwwActive) {
        subdomainsPayload = (site.subdomains || []).map((sub) => ({
          name: sub.name,
          addr: sub.addr,
          enabled: sub.name === "www" ? false : sub?.enabled,
        }));
        disabled.push("WWW Subdomain");
      } else if (sitePayload.enable === false) {
        // www is platform-managed with the root domain; no separate subdomain row.
        disabled.push("WWW Subdomain");
      }

      if (sitePayload.enable !== undefined || subdomainsPayload) {
        await updateSite(site.id, sitePayload, subdomainsPayload);
      }

      await Promise.all([getSitesForList(), getSites(setWebsiteController, listQueryOpts)]);

      if (disabled.length === 0) {
        showInfo("All protections are already disabled for this domain.");
      } else {
        showSuccess(`Disabled: ${disabled.join(", ")}.`);
      }
    } catch (err) {
      const data = err?.response?.data;
      const msg = (typeof data === "string" ? data : data?.message) || err?.message || "Failed to disable domain";
      showError(msg);
    } finally {
      setDisablingSiteId(null);
    }
  };

  const handleDeleteDomain = async () => {
    if (!hasPermission(PERMISSIONS.SITES_DELETE)) {
      showError("You don't have permission to delete sites");
      return;
    }
    setLoading(true);
    const res = await deleteSite(siteToDelete?.site_id, false);
    if (res) {
      await getSitesForList();
      await getSites(setWebsiteController, listQueryOpts);
    }
    setLoading(false);
    handleSiteToDeleteClose();
  };

  const handleDeleteSubdomain = async () => {
    setLoading(true);
    if (siteToDelete && subdomainToDelete) {
      const subdomains = siteToDelete?.subdomains
        ?.map((subdomain) => {
          return {
            name: subdomain.name,
            addr: subdomain.addr,
            enabled: subdomain?.enabled,
          };
        })
        .filter((subdomain) => subdomain?.name !== subdomainToDelete.name);
      try {
        await updateSite(siteToDelete.id, {}, subdomains);
        getSites(setWebsiteController, listQueryOpts);
      } catch (err) {
        console.error(err);
      }
    }
    setLoading(false);
    handleSubdomainToDeleteClose();
  };

  return (
    <PermissionGate permission={PERMS.SITES_READ} resourceLabel="sites">
    <>
      <Helmet title="Sites" />
      <Box sx={{ p: 6 }}>
        {!showTableLoadingSkeleton && sitesTotalCount === 0 && !hasActiveFilters ? (
          <Stack alignItems={"center"} justifyContent={"center"} spacing={4} sx={{ px: 4, py: 8, width: "100%", height: 300 }}>
            <LayerdIcon icon={UI_ICONS.manageDomain} size={54} color="neutral" />
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              No website is added yet
            </Typography>
            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
              Start by adding a domain to manage your configurations, subdomains, and security settings.
            </Typography>
            {!hasRolePermission(PERMISSIONS.SITES_WRITE) ? (
              <></>
            ) : (
              <Button
                onClick={handleAddDomain2Open}
                disabled={null === sites}
                startIcon={<LayerdIcon icon={UI_ICONS.plus} size={16} color="inherit" />}
                sx={{
                  height: 36,
                  backgroundColor: "#6371F7",
                  border: "1px solid #6371F7",
                  borderRadius: "8px",
                  fontSize: 14,
                  lineHeight: "21px",
                  color: "#fff",
                  fontWeight: "regular",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#4959ee",
                    borderColor: "#4959ee",
                    color: "#fff",
                  },
                  "&.Mui-disabled": {
                    color: colors.neutral[500],
                    backgroundColor: colors.neutral[200],
                    borderColor: colors.neutral[200],
                  },
                }}
              >
                Add Domain
              </Button>
            )}
          </Stack>
        ) : (
          <Stack spacing={6}>
            <V2PageHeader
              title="Sites"
              sx={{ pb: 0, alignItems: "center" }}
              actions={
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Button
                    onClick={async () => {
                      try {
                        const params = { ...listQueryOpts, page: 0, limit: 500 };
                        const response = await userAxios.get("/site", { params });
                        const data = response.data;
                        const rowsSrc = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
                        if (!rowsSrc.length) return;
                        const headers = ["Domain", "Origin", "Onboarded", "Status", "Enforcement"];
                        const rows = rowsSrc.map((site) => [
                          site.site_id || "",
                          site.addr || "",
                          site.created_date ? formatDate(site.created_date) : "",
                          site.isDeleted ? "Disabled" : "Active",
                          getStatusString(site.status) || "",
                        ]);
                        const csvContent = [headers, ...rows]
                          .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                          .join("\n");
                        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(blob);
                        link.download = `sites_${formatDate(new Date())}.csv`;
                        link.click();
                        URL.revokeObjectURL(link.href);
                      } catch (e) {
                        showError(e?.message || "Failed to download site list");
                      }
                    }}
                    startIcon={<LayerdIcon icon={UI_ICONS.download} size={16} color="primary" />}
                    sx={{
                      height: 36,
                      borderRadius: "8px",
                      fontSize: 14,
                      lineHeight: "21px",
                      color: "#6371F7",
                      fontWeight: "regular",
                      textTransform: "none",
                      border: "1px solid #6371F7",
                      "&:hover": {
                        backgroundColor: "rgba(99, 113, 247, 0.08)",
                        border: "1px solid #4959ee",
                        color: "#4959ee",
                      },
                    }}
                  >
                    Download site list
                  </Button>
                  {hasRolePermission(PERMISSIONS.SITES_WRITE) && (
                    <Button
                      onClick={handleAddDomain2Open}
                      disabled={null === sites}
                      startIcon={<LayerdIcon icon={UI_ICONS.plus} size={16} color="inherit" />}
                      sx={{
                        height: 36,
                        backgroundColor: "#6371F7",
                        border: "1px solid #6371F7",
                        borderRadius: "8px",
                        fontSize: 14,
                        lineHeight: "21px",
                        color: "#fff",
                        fontWeight: "regular",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#4959ee",
                          borderColor: "#4959ee",
                          color: "#fff",
                        },
                        "&.Mui-disabled": {
                          color: colors.neutral[500],
                          backgroundColor: colors.neutral[200],
                          borderColor: colors.neutral[200],
                        },
                      }}
                    >
                      Add Domain
                    </Button>
                  )}
                </Stack>
              }
            />
            <SitesFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              enforcementFilter={enforcementFilter}
              onEnforcementChange={setEnforcementFilter}
              hasActiveFilters={hasActiveFilters}
              onClearAll={handleClearFilters}
              onRefresh={refresh}
              isRefreshing={isListRefreshing}
            />
            <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px" }}>
              <Table
                sx={{
                  p: 4,
                  "& .MuiTableCell-head": { py: 3, px: 3 },
                  "& .MuiTableCell-body": { py: 2, px: 3 },
                  "& .MuiTableCell-root": { borderBottom: "none" },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "20.4%" }}>
                      <Typography sx={headTypographyStyle}>Domain</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "10.2%" }}>
                      <Typography sx={headTypographyStyle}>Origin</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "16.2%" }}>
                      <Typography sx={headTypographyStyle}>On boarded</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "15.2%" }}>
                      <Typography sx={headTypographyStyle}>Added to project</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "11.1%" }}>
                      <Typography sx={headTypographyStyle}>Status</Typography>
                    </TableCell>
                    <TableCell sx={{ width: "11.1%" }}>
                      <Typography sx={headTypographyStyle}>Enforcement</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ width: "12%" }}>
                      <Typography sx={headTypographyStyle}>Action</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {showTableLoadingSkeleton &&
                    [1, 2, 3, 4, 5]?.map((index) => (
                      <TableRow key={`ske_${index}`}>
                        <TableCell>
                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                        </TableCell>
                        <TableCell>
                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                        </TableCell>
                        <TableCell>
                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                        </TableCell>
                        <TableCell>
                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                        </TableCell>
                        <TableCell>
                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                        </TableCell>
                        <TableCell>
                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  {!showTableLoadingSkeleton &&
                    pagedVisibleSites?.map((site) => [
                      <TableRow
                        sx={{ "& .MuiTableCell-root": { backgroundColor: openIndex === site.id ? colors.primary["alpha"] : "inherit" } }}
                      >
                        <TableCell sx={{ borderTopLeftRadius: openIndex === site.id ? "8px" : "0px" }}>
                          <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <NoMaxWidthTooltip title={site.site_id} placement="bottom" arrow>
                              <Typography
                                sx={{
                                  ...cellTypographyStyle,
                                  color: openIndex === site.id ? colors.primary[300] : colors.neutral[800],
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: `calc(24vw - 150px)`,
                                }}
                              >
                                {site.site_id}
                              </Typography>
                            </NoMaxWidthTooltip>
                            {site.subdomains.length > 0 && (
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"center"}
                                onClick={() => handleClick(site.id)}
                                sx={{ cursor: "pointer", width: 24, height: 24 }}
                              >
                                {openIndex === site.id ? (
                                  <LayerdIcon icon={UI_ICONS.chevronUp} size={18} color="primaryLight" />
                                ) : (
                                  <LayerdIcon icon={UI_ICONS.chevronDown} size={18} color="neutral" />
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellTypographyStyle}>{displayCell(site.addr)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellTypographyStyle}>{displayCell(formatDate(site.created_date))}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={cellTypographyStyle}>{displayCell(site?.project_name)}</Typography>
                        </TableCell>
                        <TableCell>{getStatusLabel(site.isDeleted)}</TableCell>
                        <TableCell>
                          {getEnforcementLabel(site.status) ?? (
                            enforcementLoading ? (
                              <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                            ) : (
                              <Typography sx={{ ...cellTypographyStyle, color: colors.neutral[500] }}>—</Typography>
                            )
                          )}
                        </TableCell>
                        <TableCell sx={{ borderTopRightRadius: openIndex === site.id ? "8px" : "0px" }}>
                          {(() => {
                            const sitePermissions = getPermissionsForSite(site);
                            const actionSite = mergeResumeSiteFromList(site, site_List);
                            const actionKind = getSiteRowActionKind(
                              actionSite,
                              onboardedAtById,
                              sitePermissions.canWrite()
                            );
                            if (actionKind === "resume") {
                              return (
                                <Stack alignItems={"flex-end"} width={"100%"}>
                                  <Button
                                    onClick={(e) => handleResumeSetup(e, site)}
                                    sx={{
                                      ...containedButtonSx,
                                      height: 32,
                                      minWidth: 0,
                                      px: 1.5,
                                      fontSize: 13,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Resume setup
                                  </Button>
                                </Stack>
                              );
                            }
                            if (actionKind === "none") {
                              return null;
                            }
                            return (
                              <Stack alignItems={"flex-end"} width={"100%"}>
                                <Stack
                                  direction={"row"}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                  onClick={(e) => handleActionMainDomainOpen(e, site.id)}
                                  sx={{ cursor: "pointer", width: 24, height: 24 }}
                                >
                                  <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                                </Stack>
                              </Stack>
                            );
                          })()}
                        </TableCell>
                      </TableRow>,
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: `0px!important` }}>
                          <Collapse in={openIndex === site.id} timeout="auto" unmountOnExit>
                            <Table
                              sx={{
                                "& .MuiTableCell-head": { py: 3, px: 3 },
                                "& .MuiTableCell-body": { py: 2, px: 3 },
                                "& .MuiTableCell-root": { borderBottom: "none" },
                              }}
                            >
                              <TableBody>
                                {site.subdomains.map((subdomain, subIndex) => (
                                  <TableRow
                                    key={`site_${site.id}_${subdomain.name}`}
                                    sx={{
                                      "& .MuiTableCell-root": {
                                        backgroundColor: openIndex === site.id ? colors.primary["alpha"] : "inherit",
                                      },
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        width: "20.4%",
                                        borderBottomLeftRadius: subIndex === site.subdomains.length - 1 ? "8px" : "0px",
                                      }}
                                    >
                                      <Stack direction={"row"} alignItems={"center"} spacing={1} sx={{ pl: 7 }}>
                                        <Stack sx={{ width: 24, height: 24 }}>
                                          <LayerdIcon icon={UI_ICONS.nextLine} size={18} color="neutral" />
                                        </Stack>
                                        <Typography sx={cellTypographyStyle}>
                                          {subdomain.name}.{site.site_id}
                                        </Typography>
                                      </Stack>
                                    </TableCell>
                                    <TableCell sx={{ width: "10.2%" }}>
                                      <Typography sx={cellTypographyStyle}>{displayCell(subdomain.addr)}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "16.2%" }}>
                                      <Typography sx={cellTypographyStyle}>{displayCell(formatDate(subdomain?.updated_date))}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "15.2%" }}>
                                      <Typography sx={cellTypographyStyle}>—</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: "11.1%" }}>{getStatusLabel(!subdomain?.enabled)}</TableCell>
                                    <TableCell sx={{ width: "11.1%" }}>
                                      {getEnforcementLabel(subdomain.status) ?? (
                                        enforcementLoading ? (
                                          <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                                        ) : (
                                          <Typography sx={{ ...cellTypographyStyle, color: colors.neutral[500] }}>—</Typography>
                                        )
                                      )}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        width: "12%",
                                        borderBottomRightRadius: subIndex === site.subdomains.length - 1 ? "8px" : "0px",
                                      }}
                                    >
                                      <Stack alignItems={"flex-end"} width={"100%"}>
                                        <Stack
                                          direction={"row"}
                                          alignItems={"center"}
                                          justifyContent={"center"}
                                          onClick={(e) => handleActionSubdomainOpen(e, site.id, subdomain.name)}
                                          sx={{ cursor: "pointer", width: 24, height: 24 }}
                                        >
                                          <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                                        </Stack>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>,
                    ])}
                  {!showTableLoadingSkeleton &&
                    filteredVisibleSites?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography sx={cellTypographyStyle} textAlign="center">
                          {sitesTotalCount === 0 && !hasActiveFilters
                            ? "There are no sites"
                            : "No sites match the current filter."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {!showTableLoadingSkeleton && filteredVisibleSites.length > 0 && (
                <TablePagination
                  component="div"
                  count={sitesTotalCount}
                  page={page}
                  onPageChange={(_, next) => setPage(next)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={SITES_PAGE_SIZE_OPTIONS}
                  sx={sitesTablePaginationSx}
                />
              )}
            </Box>
          </Stack>
        )}
      </Box>
      <Menu
        anchorEl={actionMainDomainMenu.anchorEl}
        open={isActionMainDomainMenuOpen}
        onClose={handleActionMainDomainClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 200,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {(() => {
            const { siteId } = actionMainDomainMenu;
            
            // Only process if we have a valid siteId
            if (!siteId) {
              return null;
            }
            
            const site = unDeletedSites?.find((s) => s.id === siteId);
            
            if (!site) {
              return null;
            }
            
            const sitePermissions = getPermissionsForSite(site);
            
            return (
              <>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1}
                  onClick={() => {
                    setSelectedSite(site);
                    handleActionMainDomainClose();
                    handleManageDomainOpen();
                  }}
                  sx={generateMenuSx(176, 37)}
                >
                  <LayerdIcon icon={UI_ICONS.manageDomain} size={20} color="neutral" />
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                    {sitePermissions.canWrite() ? "Manage domain" : "View domain"}
                  </Typography>
                </Stack>
                {sitePermissions.canWrite() && (
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={1}
                    onClick={() => handleDisableDomain(site)}
                    sx={{
                      ...generateMenuSx(176, 37),
                      opacity: disablingSiteId === site.id ? 0.6 : 1,
                      pointerEvents: disablingSiteId === site.id ? "none" : "auto",
                    }}
                  >
                    <LayerdIcon icon={UI_ICONS.disableDomain} size={20} color="neutral" />
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                      {disablingSiteId === site.id ? "Disabling…" : "Disable domain"}
                    </Typography>
                  </Stack>
                )}
                {sitePermissions.canDelete() && (
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={1}
                    onClick={() => {
                      setSiteToDelete(site);
                      handleSiteToDeleteOpen();
                      handleActionMainDomainClose();
                    }}
                    sx={generateMenuSx(176, 37)}
                  >
                    <LayerdIcon icon={UI_ICONS.deleteDomain} size={20} color="neutral" />
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                      Delete domain
                    </Typography>
                  </Stack>
                )}
              </>
            );
          })()}
        </Box>
      </Menu>
      {actionSubdomainMenu.anchorEl && (() => {
        const { siteId } = actionSubdomainMenu;
        
        if (!siteId) {
          return null;
        }
        
        const site = unDeletedSites?.find((s) => s.id === siteId);
        
        if (!site) {
          return null;
        }
        
        const sitePermissions = getPermissionsForSite(site);
        
        return (
          <Menu
            anchorEl={actionSubdomainMenu.anchorEl}
            open={isActionSubdomainMenuOpen}
            onClose={handleActionSubdomainClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              top: 13,
              left: -12,
              "& .MuiList-root": {
                backgroundColor: "white",
                borderRadius: "8px",
                border: `solid 1px ${colors.neutral[200]}`,
                width: 160,
                boxShadow: "none",
                py: 0,
              },
              "& .MuiMenu-paper": {
                boxShadow: "none",
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={1}
                onClick={() => {
                  const { siteId, subdomainId } = actionSubdomainMenu;
                  const _site = unDeletedSites?.find((site) => site.id === siteId);
                  setSelectedSite(_site);
                  handleActionSubdomainClose();
                  handleManageDomainOpen();
                }}
                sx={generateMenuSx(136, 37)}
              >
                <LayerdIcon icon={UI_ICONS.manageDomain} size={20} color="neutral" />
                <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                  View subdomain
                </Typography>
              </Stack>
              {sitePermissions.hasPermission(PERMISSIONS.WAF_WRITE) && (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1}
                  onClick={async () => {
                    const { siteId, subdomainId } = actionSubdomainMenu;
                    const _site = unDeletedSites?.find((site) => site.id === siteId);
                    if (_site) {
                      if (_site?.subdomains?.some((subdomain) => subdomain?.name === subdomainId && subdomain?.enabled)) {
                        const subdomains = _site?.subdomains?.map((subdomain) => {
                          return {
                            name: subdomain.name,
                            addr: subdomain.addr,
                            enabled: subdomain.name === subdomainId ? false : subdomain?.enabled,
                          };
                        });
                        try {
                          await updateSite(_site.id, {}, subdomains);
                          getSites(setWebsiteController, listQueryOpts);
                        } catch (err) {
                          console.error(err);
                        }
                      }
                    }
                    handleActionSubdomainClose();
                  }}
                  sx={generateMenuSx(136, 37)}
                >
                  <LayerdIcon icon={UI_ICONS.disableWaf} size={20} color="neutral" />
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                    Disable WAF
                  </Typography>
                </Stack>
              )}
              {sitePermissions.canDelete() && (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1}
                  onClick={() => {
                    const { siteId, subdomainId } = actionSubdomainMenu;
                    const _site = unDeletedSites?.find((site) => site.id === siteId);
                    if (_site) {
                      setSiteToDelete(_site);
                      setSubdomainToDelete(_site?.subdomains?.find((subdomain) => subdomain?.name === subdomainId));
                      handleSubdomainToDeleteOpen();
                    }
                    handleActionSubdomainClose();
                  }}
                  sx={generateMenuSx(136, 37)}
                >
                  <LayerdIcon icon={UI_ICONS.deleteDomain} size={20} color="neutral" />
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                    Delete
                  </Typography>
                </Stack>
              )}
            </Box>
          </Menu>
        );
      })()}
      <ManageDomainModal
        openManageDomainModal={openManageDomainModal}
        handleManageDomainClose={handleManageDomainClose}
        selectedSite={selectedSite}
        sitePermissions={selectedSite ? getPermissionsForSite(selectedSite) : null}
      />
      <AddDomainWebsiteInformationModal
        open={openAddDomain2Modal}
        onClose={handleAddDomain2Close}
        onCreated={handleAddDomain2Created}
        resumeSite={resumeSite}
      />
      <Modal open={openSiteToDelete} onClose={handleSiteToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Site?
            </Typography>
            <Box onClick={handleSiteToDeleteClose} sx={{ cursor: "pointer" }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            You're about to permanently delete the site{" "}
            <span style={{ color: colors.neutral[1000] }}>
              <b>{siteToDelete?.domain}</b>
            </span>{" "}
            along with its configurations, firewall rules, and traffic settings.
          </Typography>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            This action cannot be undone.
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleSiteToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDeleteDomain} disabled={loading} label="Delete" />
          </Stack>
        </Stack>
      </Modal>
      <Modal open={openSubdomainToDelete} onClose={handleSubdomainToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Subdomain?
            </Typography>
            <Box onClick={handleSubdomainToDeleteClose} sx={{ cursor: "pointer" }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            You're about to permanently delete the subdomain{" "}
            <span style={{ color: colors.neutral[1000] }}>
              <b>{subdomainToDelete?.domain}</b>
            </span>{" "}
            along with its configurations, firewall rules, and traffic settings.
          </Typography>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            This action cannot be undone.
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleSubdomainToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDeleteSubdomain} disabled={loading} label="Delete" />
          </Stack>
        </Stack>
      </Modal>
    </>
    </PermissionGate>
  );
};
export default NewSitesPage;
